import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Switch, Button, Tabs, Tooltip } from 'antd';
import { MdOutlineEdit, MdOutlineRemoveRedEye, MdContentPaste, MdCode } from 'react-icons/md';
import { toast } from 'react-toastify';
import { BreathAPIs } from '../../APIs/BreathAPIs';
import HtmlEditorField from './HtmlEditorField';

const { TextArea } = Input;

const MessageTemplateForm = ({ visible, template, onClose, onSuccess }) => {
    const [form] = Form.useForm();
    const [isLoading, setIsLoading] = useState(false);
    const [channel, setChannel] = useState('email');
    const [bodyValue, setBodyValue] = useState('');
    const [activeTab, setActiveTab] = useState('editor');
    const [isHtmlMode, setIsHtmlMode] = useState(false);
    const isEdit = !!template;

    const mainTemplate = {
        header: '<body style="background:#f5f5f5;font-size:14px;padding:4% 2% 2% 2%;font-family:Helvetica"><div style="background:#fff;border-radius:10px;padding:30px;max-width:600px;margin:auto;box-shadow:0 2px 8px rgba(0,0,0,0.05);"><div style="text-align:center;line-height:38px;"><img style="width:120px;margin-bottom:10px;" src="https://d3m0dzbeurmsd7.cloudfront.net/images/logo.png" alt="BreatheEV Logo"><h2 style="margin:10px 0;color:#222;">Head</h2></div><div style="font-size:14px;line-height:28px;color:#444;margin-top:20px;">',
        body:   '<p>Hey {{first name}}!!</p><p>Body.</p>',
        footer: '</div><div style="width:100%;text-align:right;line-height:24px;color:#444;margin-top:30px;">Safe Travels,<br><strong>The BreatheEV Charging Team</strong></div><div style="font-size:12px;line-height:21px;margin-top:25px;color:#777;text-align:center;">Reply to this email with any questions or if you just want to say hi!<br>For immediate support call +1 (844) 252-2083</div></div></body>',
    };

    const isAlreadyWrapped = (body) => {
        if (!body) return false;
        return body.includes('<body');
    };

    const mergeVariables = (bodyText) => {
        const found = [...new Set(
            [...bodyText.matchAll(/{{([^}]+)}}/g)].map((m) => m[1].trim())
        )];
        if (!found.length) return;
        const existing = form.getFieldValue('variables') || [];
        form.setFieldValue('variables', [...new Set([...existing, ...found])]);
    };

    const pasteTemplate = () => {
        const starter = `${mainTemplate.header}\n\n ${mainTemplate.body} <!-- Your email content here -->\n\n${mainTemplate.footer}`;
        setBodyValue(starter);
        form.setFieldValue('body', starter);
        mergeVariables(starter);
        toast.success('Base template pasted — edit the content between header and footer.');
    };

    const wrapWithTemplate = () => {
        const current = bodyValue.trim();
        const wrapped = `${mainTemplate.header}\n\n${current}\n\n${mainTemplate.footer}`;
        setBodyValue(wrapped);
        form.setFieldValue('body', wrapped);
        mergeVariables(wrapped);
        toast.success('Body wrapped with base template.');
    };

    useEffect(() => {
        if (visible) {
            setIsHtmlMode(false);
            if (template) {
                setChannel(template.channel);
                setBodyValue(template.body || '');
                setActiveTab('editor');
                form.setFieldsValue({
                    key: template.key,
                    channel: template.channel,
                    description: template.description,
                    subject: template.subject,
                    body: template.body,
                    variables: template.variables || [],
                    isActive: template.isActive !== false,
                });
            } else {
                setChannel('email');
                setBodyValue('');
                setActiveTab('editor');
                form.resetFields();
                form.setFieldsValue({ channel: 'email', isActive: true });
            }
        }
    }, [visible, template]);

    const handleCancel = () => {
        form.resetFields();
        setBodyValue('');
        setIsHtmlMode(false);
        onClose();
    };

    const handleSubmit = async (values) => {
        setIsLoading(true);
        try {
            if (isEdit) {
                await BreathAPIs.updateMessageTemplate(template._id, values);
                toast.success('Template updated.');
            } else {
                await BreathAPIs.createMessageTemplate(values);
                toast.success('Template created.');
            }
            form.resetFields();
            setBodyValue('');
            onSuccess();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error saving template.');
        } finally {
            setIsLoading(false);
        }
    };

    const renderSmsPreview = (body) => {
        if (!body) return <span style={{ color: '#aaa' }}>Nothing to preview yet.</span>;
        const parts = body.split(/{{\s*(\w+)\s*}}/g);
        return parts.map((part, i) => {
            if (i % 2 === 1) {
                return (
                    <span
                        key={i}
                        style={{
                            background: '#fff7e6',
                            color: '#d46b08',
                            borderRadius: 4,
                            padding: '1px 6px',
                            fontWeight: 500,
                            border: '1px dashed #ffc069',
                            fontSize: 13,
                        }}
                    >
                        {`{{${part.trim()}}}`}
                    </span>
                );
            }
            return <span key={i}>{part}</span>;
        });
    };

    const detectedVariables = () => {
        if (!bodyValue) return [];
        const matches = [...bodyValue.matchAll(/{{\s*(\w+)\s*}}/g)];
        return [...new Set(matches.map((m) => m[1].trim()))];
    };

    const already = isAlreadyWrapped(bodyValue);

    const bodyLabel = (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <span>
                {channel === 'email'
                    ? 'Body (HTML — use {{variable}} for placeholders)'
                    : 'Body (plain text — use {{variable}} for placeholders)'}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft:10 }}>
                {channel === 'email' && !isHtmlMode && (
                    isEdit ? (
                        <Tooltip title={already ? 'Body is already wrapped with the base template' : 'Wrap current body content with the BreatheEV header and footer'}>
                            <Button
                                size="small"
                                icon={<MdContentPaste style={{ fontSize: 14, marginRight: 4 }} />}
                                onClick={wrapWithTemplate}
                                disabled={already}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontSize: 12,
                                    color: already ? undefined : '#1677ff',
                                    borderColor: already ? undefined : '#1677ff',
                                }}
                            >
                                {already ? 'Already Wrapped' : 'Wrap with Base Template'}
                            </Button>
                        </Tooltip>
                    ) : (
                        <Tooltip title="Paste the BreatheEV base email template (header + footer) as a starting point">
                            <Button
                                size="small"
                                icon={<MdContentPaste style={{ fontSize: 14, marginRight: 4 }} />}
                                onClick={pasteTemplate}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontSize: 12,
                                    color: '#1677ff',
                                    borderColor: '#1677ff',
                                }}
                            >
                                Paste Base Template
                            </Button>
                        </Tooltip>
                    )
                )}
                {channel === 'email' && (
                    <Tooltip title={isHtmlMode ? 'Switch back to raw HTML source' : 'Switch to visual HTML editor'}>
                        <Button
                            size="small"
                            icon={<MdCode style={{ fontSize: 14, marginRight: 4 }} />}
                            onClick={() => setIsHtmlMode((m) => !m)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: 12,
                                color: isHtmlMode ? '#52c41a' : undefined,
                                borderColor: isHtmlMode ? '#52c41a' : undefined,
                            }}
                        >
                            {isHtmlMode ? 'Source' : 'HTML Editor'}
                        </Button>
                    </Tooltip>
                )}
            </div>
        </div>
    );

    const editorContent = (
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
                label="Key"
                name="key"
                rules={[{ required: true, message: 'Key is required' }]}
                tooltip="Unique identifier used in code e.g. wallet_reload_confirmation"
            >
                <Input
                    placeholder="e.g. wallet_reload_confirmation"
                    disabled={isEdit}
                />
            </Form.Item>

            <Form.Item
                label="Channel"
                name="channel"
                rules={[{ required: true, message: 'Channel is required' }]}
            >
                <Select
                    disabled={isEdit}
                    onChange={(val) => {
                        setChannel(val);
                        setBodyValue('');
                        form.setFieldValue('body', '');
                    }}
                    options={[
                        { value: 'email', label: 'Email' },
                        { value: 'sms', label: 'SMS' },
                    ]}
                />
            </Form.Item>

            <Form.Item label="Description" name="description">
                <Input placeholder="Short description shown in the admin list" />
            </Form.Item>

            {channel === 'email' && (
                <Form.Item
                    label="Subject"
                    name="subject"
                    rules={[{ required: channel === 'email', message: 'Subject is required for email templates' }]}
                >
                    <Input placeholder="e.g. Success! Your BreatheEV Wallet has been reloaded" />
                </Form.Item>
            )}

            <Form.Item
                label={bodyLabel}
                name="body"
                rules={[{ required: true, message: 'Body is required' }]}
            >
                {isHtmlMode ? (
                    <HtmlEditorField
                        onBodyChange={setBodyValue}
                        onVarInsert={(name) => {
                            const existing = form.getFieldValue('variables') || [];
                            if (!existing.includes(name)) {
                                form.setFieldValue('variables', [...existing, name]);
                            }
                        }}
                    />
                ) : (
                    <TextArea
                        rows={channel === 'email' ? 16 : 6}
                        placeholder={
                            channel === 'email'
                                ? '<p>Hi {{firstName}},</p>\n<p>Your wallet has been credited <b>{{total}}</b>.</p>'
                                : 'Hi {{firstName}}, your BreatheEV session starts in 15 minutes.'
                        }
                        style={{ fontFamily: 'monospace', fontSize: 13 }}
                        onChange={(e) => setBodyValue(e.target.value)}
                    />
                )}
            </Form.Item>

            <Form.Item
                label="Variables"
                name="variables"
                tooltip="List the placeholder names used in the body/subject — helps the admin UI show what values are needed"
            >
                <Select
                    mode="tags"
                    placeholder="Type a variable name and press Enter e.g. firstName"
                    tokenSeparators={[',']}
                    open={false}
                />
            </Form.Item>

            <Form.Item label="Active" name="isActive" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>

            <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                style={{ width: '100%' }}
            >
                {isEdit ? 'Update Template' : 'Create Template'}
            </Button>
        </Form>
    );

    const previewContent = (
        <div>
            {channel === 'email' ? (
                bodyValue ? (
                    <iframe
                        key={bodyValue}
                        title="email-body-preview"
                        srcDoc={bodyValue}
                        style={{
                            width: '100%',
                            height: 520,
                            border: '1px solid #eee',
                            borderRadius: 6,
                            background: '#fff',
                        }}
                    />
                ) : (
                    <div style={{
                        height: 520,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 16,
                        border: '1px dashed #d9d9d9',
                        borderRadius: 6,
                        color: '#aaa',
                        fontSize: 14,
                    }}>
                        <span>Start typing your HTML body to see a live preview.</span>
                        {!isEdit && (
                            <Button
                                icon={<MdContentPaste style={{ fontSize: 14, marginRight: 4 }} />}
                                onClick={() => { pasteTemplate(); setActiveTab('editor'); }}
                                style={{ display: 'flex', alignItems: 'center', color: '#1677ff', borderColor: '#1677ff' }}
                            >
                                Paste Base Template & Edit
                            </Button>
                        )}
                    </div>
                )
            ) : (
                <div style={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: 2,
                    padding: 16,
                    border: '1px solid #eee',
                    borderRadius: 6,
                    background: '#fafafa',
                    fontSize: 14,
                    minHeight: 120,
                }}>
                    {renderSmsPreview(bodyValue)}
                </div>
            )}

            {detectedVariables().length > 0 && (
                <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>
                        Variables detected in body:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {detectedVariables().map((v) => (
                            <span
                                key={v}
                                style={{
                                    background: '#fff7e6',
                                    color: '#d46b08',
                                    borderRadius: 4,
                                    padding: '2px 10px',
                                    fontWeight: 500,
                                    border: '1px dashed #ffc069',
                                    fontSize: 13,
                                }}
                            >
                                {`{{${v}}}`}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <Modal
            title={isEdit ? `Edit Template: ${template?.key}` : 'New Template'}
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width={900}
            destroyOnClose
        >
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                style={{ marginBottom: 0 }}
                items={[
                    {
                        key: 'editor',
                        label: (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <MdOutlineEdit size={15} /> Editor
                            </span>
                        ),
                        children: editorContent,
                    },
                    {
                        key: 'preview',
                        label: (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <MdOutlineRemoveRedEye size={15} /> Preview
                            </span>
                        ),
                        children: previewContent,
                    },
                ]}
            />
        </Modal>
    );
};

export default MessageTemplateForm;